import { useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { refreshUserToken } from '../store/authSlice';
import apiService from '../services/api';

interface ActivityTracker {
  lastActivity: number;
  isIdle: boolean;
  idleTimeout: number;
}

export const useTokenRefresh = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const activityTrackerRef = useRef<ActivityTracker>({
    lastActivity: Date.now(),
    isIdle: false,
    idleTimeout: 30 * 60 * 1000, // 30 minutes
  });

  // Track user activity
  const updateActivity = useCallback(() => {
    const tracker = activityTrackerRef.current;
    tracker.lastActivity = Date.now();
    tracker.isIdle = false;
  }, []);

  // Check if user is idle
  const isUserIdle = useCallback(() => {
    const tracker = activityTrackerRef.current;
    const timeSinceActivity = Date.now() - tracker.lastActivity;
    return timeSinceActivity > tracker.idleTimeout;
  }, []);

  // Proactive token refresh
  const refreshTokenIfNeeded = useCallback(async () => {
    try {
      // Don't refresh if user is idle
      if (isUserIdle()) {
        console.log('🔄 [TokenRefresh] User is idle, skipping token refresh');
        return;
      }

      // Check if token is expiring soon (within 5 minutes)
      if (apiService.isTokenExpiringSoon(5)) {
        console.log('🔄 [TokenRefresh] Token expiring soon, refreshing...');
        await dispatch(refreshUserToken()).unwrap();
        console.log('✅ [TokenRefresh] Token refreshed successfully');
      }
    } catch (error) {
      console.error('❌ [TokenRefresh] Failed to refresh token:', error);
      // Clear session and redirect to login on refresh failure
      apiService.clearTokens();
      navigate('/sign_in');
    }
  }, [dispatch, navigate, isUserIdle]);

  // Schedule next refresh check
  const scheduleNextRefresh = useCallback(() => {
    // Clear existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    const timeUntilExpiry = apiService.getTimeUntilExpiry();
    if (timeUntilExpiry <= 0) return;

    // Schedule refresh 5 minutes before expiry
    const refreshTime = Math.max(0, timeUntilExpiry - (5 * 60 * 1000));
    
    console.log(`🕐 [TokenRefresh] Next refresh scheduled in ${Math.round(refreshTime / 60000)} minutes`);
    
    refreshTimeoutRef.current = setTimeout(() => {
      refreshTokenIfNeeded().then(() => {
        // Schedule next refresh after successful refresh
        scheduleNextRefresh();
      });
    }, refreshTime);
  }, [refreshTokenIfNeeded]);

  // Set up activity listeners
  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      updateActivity();
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [updateActivity]);

  // Main effect - set up token refresh monitoring
  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
    if (!isAuthenticated) return;  // Already present, keep this check

    // Add check for sign-in page
    const currentPath = window.location.pathname;
    if (currentPath === '/sign_in' || currentPath === '/reset-password') {
      return; // Don't run token refresh on auth pages
    }

    // Initial check and schedule
    refreshTokenIfNeeded().then(() => {
      scheduleNextRefresh();
    });

    // Cleanup on unmount
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [refreshTokenIfNeeded, scheduleNextRefresh]);

  // Return activity tracking methods for external use
  return {
    updateActivity,
    isUserIdle,
  };
};
